import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import { useRoutes } from 'react-router';

import type { RouteDefinition } from './createRouteDefinition';
import { useRouteRootConfigContext } from './feature-routing/routeConfigContext';

type Options = {
    routes: RouteDefinition[];
};

function stripStarFromPath(path: string): string {
    if (path.endsWith('/*')) {
        return path.replace(/^\//, '').replace(/\/\*$/, '').replace(/\*$/, '');
    }
    return path;
}
/**
 * Use rootId if provided, otherwise use pathValue that contains /*.
 * Returned path value is stripped from leading slashes and /* suffix.
 *
 * This identifier is later used for dynamic look up, so the type referencing it must have the same value.
 */
function computeRootIdFromPath(rootId: string | undefined, pathValue: string) {
    if (rootId) {
        return rootId;
    }

    return stripStarFromPath(pathValue);
}

function mapRootIdsToPath(routesAfterCondition: RouteDefinition[]): Record<string, string> {
    return routesAfterCondition.reduce(
        (acc, route) => {
            const rootId = computeRootIdFromPath(route.rootId, route.path);
            if (rootId) {
                acc[rootId] = stripStarFromPath(route.path);
            }

            return acc;
        },
        {} as Record<string, string>,
    );
}

/** accepts an array of Route definitions with a condition property, removes those that have a
 * condition returning false, and will render the best matching route based on the current route */
export const useRouteDefinitions = ({ routes }: Options): ReactElement | null => {
    const { setValue, value } = useRouteRootConfigContext();
    const { routesAfterCondition, routesRootMap } = useMemo(() => {
        const filteredRoutes = filterRoutesWithFalseCondition(routes);
        return {
            routesAfterCondition: filteredRoutes,
            routesRootMap: mapRootIdsToPath(filteredRoutes),
        };
    }, [routes]);

    useEffect(() => {
        setValue(routesRootMap);
    }, [routesRootMap]);
    const routeDefinitions = useRoutes(routesAfterCondition);

    if (!value) {
        return null;
    }

    return routeDefinitions;
};

export function filterRoutesWithFalseCondition(routes: RouteDefinition[]): RouteDefinition[] {
    return routes.filter((route) => route.condition?.() !== false);
}
